import { Hooks } from "ky";
import { useStore } from "../../store";
import { authService } from "./auth.service";
import { TAuthRefreshAccessTokenResponsePayload } from "./auth.types";
import { TApiServiceError } from "../apiService";
import { TAsyncTuple } from "../../utils";
import { getCookie, setSubdomainCookie } from "../../utils/cookies";
import { resolveTrustedRedirectUrl } from "@/utils/trustedOrigins";

const ACCESS_TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

// Mutex to prevent concurrent refresh token calls
let refreshTokenPromise: Promise<
  TAsyncTuple<TApiServiceError, TAuthRefreshAccessTokenResponsePayload>
> | null = null;

// Guard to prevent multiple concurrent redirects to login
let isRedirectingToLogin = false;

/**
 * Clears persisted credentials without resetting the current route's in-memory
 * auth state, then redirects to login. Avoiding the in-memory reset prevents
 * the permission guard from flashing its restricted fallback.
 * Uses window.location.origin to preserve the tenant subdomain.
 */
function forceLogoutRedirect(): void {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  useStore.getState().clearAuthStorage();
  window.location.replace(`${window.location.origin}/auth/login?logout=true`);
}

function getStoredToken(name: "access_token" | "refresh_token"): string {
  return localStorage?.getItem(name) ?? getCookie(name) ?? "";
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload = ""] = token.split(".");
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalizedPayload));
  } catch {
    return null;
  }
}

function getTokenRemainingMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;

  if (typeof exp !== "number") {
    return null;
  }

  return exp * 1000 - Date.now();
}

function isTokenNearExpiry(token: string): boolean {
  const remainingMs = getTokenRemainingMs(token);
  return (
    remainingMs !== null && remainingMs <= ACCESS_TOKEN_REFRESH_THRESHOLD_MS
  );
}

function persistAuthState(
  accessToken: string,
  refreshToken: string,
  accessTokenLifetimeSeconds?: number | null,
  refreshTokenLifetimeSeconds?: number | null,
): void {
  localStorage.setItem("access_token", accessToken);
  localStorage.setItem("refresh_token", refreshToken);

  if (typeof accessTokenLifetimeSeconds === "number") {
    setSubdomainCookie("access_token", accessToken, {
      maxAgeSeconds: Math.max(accessTokenLifetimeSeconds, 0),
    });
  }

  if (typeof refreshTokenLifetimeSeconds === "number") {
    setSubdomainCookie("refresh_token", refreshToken, {
      maxAgeSeconds: Math.max(refreshTokenLifetimeSeconds, 0),
    });
  }
}

export async function ensureFreshAccessToken(
  forceRefresh = false,
): Promise<string | null> {
  if (isRedirectingToLogin) {
    return null;
  }

  const accessToken = getStoredToken("access_token");
  const refreshToken = getStoredToken("refresh_token");

  if (!accessToken || !refreshToken) {
    return null;
  }

  if (!forceRefresh && !isTokenNearExpiry(accessToken)) {
    return accessToken;
  }

  if (refreshTokenPromise) {
    const { data } = await refreshTokenPromise;
    return data?.access ?? null;
  }

  refreshTokenPromise = authService.refreshAccessToken(
    { refresh: refreshToken },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  try {
    const { data } = await refreshTokenPromise;
    if (!data?.access) {
      forceLogoutRedirect();
      return null;
    }
    const latestRefreshToken = data.refresh ?? refreshToken;

    persistAuthState(
      data.access,
      latestRefreshToken,
      Math.floor(Math.max((getTokenRemainingMs(data.access) ?? 0) / 1000, 0)),
      Math.floor(Math.max((getTokenRemainingMs(refreshToken) ?? 0) / 1000, 0)),
    );

    return data.access;
  } catch {
    forceLogoutRedirect();
    return null;
  } finally {
    refreshTokenPromise = null;
  }
}

export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const initialAccessToken =
    (await ensureFreshAccessToken(false)) ?? getStoredToken("access_token");
  const request = new Request(input, init);

  if (initialAccessToken) {
    request.headers.set("Authorization", `Bearer ${initialAccessToken}`);
  }

  let response = await fetch(request);

  if (response.status === 502) {
    throw new Error(
      "502 - Server is updating. Please try again in a few moments.",
    );
  }

  if (response.status === 401 && !isRedirectingToLogin) {
    const refreshedAccessToken = await ensureFreshAccessToken(true);
    if (refreshedAccessToken) {
      const retryRequest = new Request(input, init);
      retryRequest.headers.set(
        "Authorization",
        `Bearer ${refreshedAccessToken}`,
      );
      response = await fetch(retryRequest);

      if (response.status === 502) {
        throw new Error(
          "502 - Server is updating. Please try again in a few moments.",
        );
      }
    }
  }

  if (
    response.ok &&
    response.headers.get("X-Token-Refresh-Required") === "true"
  ) {
    void ensureFreshAccessToken(true).catch(() => undefined);
  }

  return response;
}

export const withAuthHooks: Hooks = {
  beforeRequest: [
    async function (request) {
      const accessToken =
        (await ensureFreshAccessToken(false)) ?? getStoredToken("access_token");

      if (accessToken) {
        request.headers.set("Authorization", `Bearer ${accessToken}`);
      }
    },
  ],
  afterResponse: [
    async function (request, _, response) {
      if (response.status === 301) {
        const location = response.headers.get("Location");
        if (location) {
          const safeUrl = resolveTrustedRedirectUrl(location);
          if (safeUrl) {
            window.location.replace(safeUrl);
          } else if (import.meta.env.DEV) {
            console.warn(
              "[auth.ky.hooks] Ignored 301 redirect to untrusted location:",
              location,
            );
          }
        }
        return response;
      }
      if (response.status === 502) {
        const err = new Error(
          "502 - Server is updating. Please try again in a few moments.",
        ) as Error & { errorResponse?: any; status?: number };
        err.errorResponse = {
          message:
            "502 - Server is updating. Please try again in a few moments.",
        };
        err.status = response.status;
        throw err;
      }

      if (response.status === 401) {
        if (isRedirectingToLogin) return;

        const accessToken = await ensureFreshAccessToken(true);
        if (accessToken) {
          request.headers.set("Authorization", `Bearer ${accessToken}`);
          return await fetch(request.clone());
        }

        forceLogoutRedirect();
      }

      if (
        response.ok &&
        response.headers.get("X-Token-Refresh-Required") === "true"
      ) {
        void ensureFreshAccessToken(true).catch(() => undefined);
      }

      if (!response.ok) {
        let errorBody: { message?: string };
        try {
          const parseTarget =
            typeof response.clone === "function" ? response.clone() : response;
          errorBody = await parseTarget.json();
        } catch (err) {
          if (
            err instanceof SyntaxError ||
            (err as Error)?.name === "SyntaxError"
          ) {
            return response;
          }
          throw err;
        }

        const err = new Error(
          errorBody.message ?? "Request failed",
        ) as Error & { errorResponse?: any; status?: number };
        err.errorResponse = errorBody;
        err.status = response.status;
        throw err;
      }

      return response;
    },
  ],
};
